import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";

import { ListLoadingState } from "../../components/ListLoadingState";
import { OpenShiftCardContainer } from "../../Shift/Open/CardContainer";
import { type OpenShiftWorkplace } from "../../Shift/Open/types";
import { convertShiftToSlotId } from "../../Shift/Open/useGetShiftsSlotV2";
import { useOpenShiftListDataContext } from "../../Shift/Open/useOpenShiftListDataContext";
import { useVirtualShiftListContext } from "../../Shift/Open/useVirtualShiftListContext/useVirtualShiftListContext";
import { ShiftVirtualizedList } from "../../Shift/Open/VirtualizedList";
import { WorkplaceOpenShiftsEmptyState } from "./EmptyState";

interface WorkplaceOpenShiftsListProps {
  scrollRef: React.RefObject<HTMLDivElement>;
  workplace?: OpenShiftWorkplace;
}

export function WorkplaceOpenShiftsList(props: WorkplaceOpenShiftsListProps) {
  const { workplace, scrollRef } = props;

  const { listRef, virtualShiftListItems } = useVirtualShiftListContext();

  const isListEmpty = virtualShiftListItems.length === 0;

  const {
    onClickOpenShift,
    shiftsBlockingRequirementsContextMap,
    isOpenShiftsLoading,
    isOpenShiftsError,
    isOpenShiftsSuccess,
    canLoadMore,
    loadMore,
    favoriteWorkplaceIds,
    isLoadingMore,
    shiftsSlotsMap,
    friendsMap,
    recentColleaguesMap,
    shiftAuctionsMap,
    handleRangeChanged,
  } = useOpenShiftListDataContext();

  if (isOpenShiftsError) {
    return <Text>Error loading shifts</Text>;
  }

  if (isOpenShiftsLoading) {
    return (
      <ListLoadingState
        texts={[
          "Finding you the best-paying shifts at this facility",
          "Analyzing shift opportunities just for you",
          "Your perfect shift is just a moment away",
          "Crunching the numbers to maximize your earnings",
          "Digging deep into facility schedules",
          "Searching high and low for the best shifts at this location",
          "Unlocking your next great opportunity at this facility",
        ]}
      />
    );
  }

  if (isOpenShiftsSuccess && isListEmpty) {
    return <WorkplaceOpenShiftsEmptyState />;
  }

  return (
    <ShiftVirtualizedList
      ref={listRef}
      scrollRef={scrollRef}
      items={virtualShiftListItems}
      isLoadingMore={isLoadingMore}
      renderItem={(item) => {
        if (item.type !== "open-shift") {
          // Skip non-open-shift items (like in-app ads)
          return null;
        }

        const shift = item;
        if (!isDefined(workplace)) {
          return null;
        }

        const shiftAuctionId = shift.relationships.shiftAuction?.data.id;
        const shiftAuction = shiftAuctionId ? shiftAuctionsMap.get(shiftAuctionId) : undefined;

        return (
          <OpenShiftCardContainer
            key={shift.id}
            workers={
              shiftsSlotsMap
                .get(
                  convertShiftToSlotId({
                    start: shift.attributes.start,
                    end: shift.attributes.end,
                    facilityId: workplace.id,
                  })
                )
                ?.shiftAssignments.filter((assignment) => assignment.shiftId !== shift.id)
                .map(({ worker }) => worker) ?? []
            }
            shift={shift}
            workplace={workplace}
            isFavoriteWorkplace={favoriteWorkplaceIds.has(workplace.id)}
            blockingRequirementsContext={(() => {
              const context = shiftsBlockingRequirementsContextMap.get(shift.id);
              return context === null ? undefined : context;
            })()}
            shiftAuction={shiftAuction}
            friendsMap={friendsMap}
            recentColleaguesMap={recentColleaguesMap}
            onClick={({ offer }) => {
              onClickOpenShift({ shift, workplace, offer, shiftAuction });
            }}
          />
        );
      }}
      onEndReached={() => {
        if (canLoadMore) {
          loadMore();
        }
      }}
      onRangeChanged={handleRangeChanged}
    />
  );
}
